import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { 
  PartyPopper, 
  Sparkles,
  Bot,
  Table,
  CheckSquare,
  Dna,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

interface TourCompletionModalProps {
  onClose: () => void;
}

const quickLinks = [
  { 
    icon: Bot, 
    label: 'Conversar com Agentes', 
    path: '/ferramentas',
    description: 'Comece uma conversa com IA'
  },
  { 
    icon: Table, 
    label: 'Ver Planilhas', 
    path: '/ferramentas',
    description: 'Organize seu negócio'
  },
  { 
    icon: CheckSquare, 
    label: 'Criar Checklist', 
    path: '/smartcheck',
    description: 'Defina suas metas'
  },
  { 
    icon: Dna, 
    label: 'Configurar DNA', 
    path: '/dashboard',
    description: 'Personalize a IA'
  }
];

// Confetti particle component
function ConfettiParticle({ delay }: { delay: number }) {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
  const color = colors[Math.floor(Math.random() * colors.length)];
  const left = Math.random() * 100;
  const duration = 2 + Math.random() * 2;

  return (
    <motion.div
      initial={{ y: -20, x: 0, opacity: 1, rotate: 0 }}
      animate={{ 
        y: 400, 
        x: (Math.random() - 0.5) * 100,
        opacity: 0,
        rotate: Math.random() * 720 - 360
      }}
      transition={{ 
        duration, 
        delay,
        ease: 'easeIn'
      }}
      className="absolute w-3 h-3 rounded-sm"
      style={{ 
        left: `${left}%`,
        backgroundColor: color,
        top: 0
      }}
    />
  );
}

export function TourCompletionModal({ onClose }: TourCompletionModalProps) {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-hidden"
      >
        {/* Confetti */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 50 }).map((_, i) => (
              <ConfettiParticle key={i} delay={i * 0.05} />
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <Card className="relative max-w-lg w-full overflow-hidden bg-card border-border shadow-2xl">
            {/* Decorative background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 pointer-events-none" />
            
            {/* Content */}
            <div className="relative p-8">
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{ 
                    duration: 0.6,
                    delay: 0.2
                  }}
                  className="p-4 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/30"
                >
                  <PartyPopper className="h-12 w-12 text-primary" />
                </motion.div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-center text-foreground mb-2">
                Tour Concluído! 🎉
              </h2>
              
              <p className="text-muted-foreground text-center mb-6">
                Você conheceu os principais recursos do MarketPro Tools. 
                Agora é hora de colocar a mão na massa!
              </p>

              {/* Quick links */}
              <div className="space-y-2 mb-6">
                <p className="text-sm font-medium text-foreground mb-3">
                  Por onde começar?
                </p>
                {quickLinks.map((link, index) => (
                  <motion.button
                    key={link.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    onClick={() => handleNavigate(link.path)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border hover:bg-muted hover:border-primary/30 transition-all group text-left"
                  >
                    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <link.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground block">
                        {link.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {link.description}
                      </span>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </motion.button>
                ))}
              </div>

              {/* Main action */}
              <Button
                size="lg"
                onClick={onClose}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Começar a Usar
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>

              {/* Tip */}
              <p className="text-xs text-muted-foreground text-center mt-4">
                💡 Dica: Você pode rever o tour a qualquer momento nas configurações
              </p>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
